import { useMemo, useState } from 'react'

import { AppBar, Stack, Tab, Tabs, Tooltip } from '@mui/material'
import { Link, useParams } from 'react-router-dom'

import { TabPanel } from 'components/common'
import { Mode, PARAM_NEW, Roles } from 'constants/index'
import { isNumber, CloneProps, useHashToSelectTab } from 'helpers'
import { useDriverRole } from 'hooks'
import { PermissionType, Restricted, useIsAllowedTo } from 'providers/PermissionProvider'


import { Contacts } from './Contacts'
import { DriverAccountsPayable } from './DriverAccountsPayable'
import { DriverDetails } from './DriverDetails'
import { BookingDriverRatingsTab } from './DriverDetails'
import { DriverInvoices } from './DriverInvoices'
import { useGetCurrentDriver } from './hooks'
import { SubDrivers } from './SubDrivers'
import { Vehicles } from './Vehicles'

enum TabId {
  Details = 0,
  Vehicles,
  SubDrivers,
  Contacts,
  Invoices,
  AccountsPayable,
  Ratings,
}

type TabType = {
  id: TabId
  label: string
  hash: string
  policy: PermissionType | null
}

const TabsList = (): Array<TabType> => [
  {
    id: TabId.Details,
    label: 'Company Details',
    hash: '$company-details',
    policy: null,
  },
  {
    id: TabId.Vehicles,
    label: 'Vehicles',
    hash: '#vehicles',
    policy: null,
  },
  {
    id: TabId.SubDrivers,
    label: 'Sub-drivers',
    hash: '#sub-drivers',
    policy: null,
  },
  {
    id: TabId.Contacts,
    label: 'Contacts',
    hash: '#contacts',
    policy: 'ViewDriverContactList',
  },
  {
    id: TabId.Invoices,
    label: 'Invoices',
    hash: '#invoices',
    policy: 'ViewDriverInvoices' as PermissionType,
  },
  {
    id: TabId.AccountsPayable,
    label: 'Accounts Payable',
    hash: '#accountsPayable',
    policy: 'ViewDriverAccountsPayableRecords' as PermissionType,
  },
  {
    id: TabId.Ratings,
    label: 'Ratings',
    hash: '#ratings',
    policy: 'ViewDriverRatings' as PermissionType,
  },
]

function commonProps(index: TabId) {
  return {
    id: `driver-tab-${index}`,
    'aria-controls': `driver-tabpanel-${index}`,
  }
}

function DriverTab() {
  const [tabValue, setTabValue] = useState<number | false>(false)
  const { id } = useParams<{ id?: string }>()
  const isAllowedTo = useIsAllowedTo()

  const tabList = TabsList()

  const primaryTab = tabList[0]

  const filteredSecondaryTabs = useMemo(
    () =>
      tabList.filter((tab, index) => {
        return index !== 0 && (tab.policy ? isAllowedTo(tab.policy) : true)
      }),
    [isAllowedTo, tabList],
  )

  useHashToSelectTab(tabList, setTabValue)

  let mode: number | undefined

  if (id === PARAM_NEW) {
    mode = Mode.Create
  } else if (id && isNumber(id)) {
    mode = Mode.Update
  }

  let driverId: number | null = mode === Mode.Update ? Number(id) : null

  //if user role is driver
  const { data, isDriver } = useGetCurrentDriver()

  const driverRole = useDriverRole(Number(driverId))

  const isDriverSubcontractor = driverRole === Roles.driverSubcontractor

  if (isDriver) {
    driverId = Number(data?.driverProfile.id)
    mode = Mode.Update
  }

  return (
    <div>
      <AppBar position='static' color='default'>
        <Tabs
          value={tabValue}
          aria-label='driver tabs'
          variant='scrollable'
          TabIndicatorProps={{
            style: {
              transition: 'none',
            },
          }}>
          <Tab
            value={primaryTab.id}
            label={primaryTab.label}
            {...commonProps(primaryTab.id)}
            component={Link}
            to={{ hash: primaryTab.hash }}
            sx={{ display: 'block' }}
          />
          {mode === Mode.Create ? (
            <CloneProps>
              {(tabProps) => (
                <Tooltip title='Available only for existing driver.'>
                  <Stack direction='row'>
                    {filteredSecondaryTabs.map((tab) => (
                      <Tab
                        value={tab.id}
                        {...commonProps(tab.id)}
                        {...tabProps}
                        label={tab.label}
                        key={tab.id}
                        disabled
                      />
                    ))}
                  </Stack>
                </Tooltip>
              )}
            </CloneProps>
          ) : (
            filteredSecondaryTabs.map((tab) => (
              <Tab
                value={tab.id}
                label={tab.label}
                key={tab.id}
                {...commonProps(tab.id)}
                component={Link}
                to={{ hash: tab.hash }}
                disabled={tab.id === TabId.SubDrivers && isDriverSubcontractor}
              />
            ))
          )}
        </Tabs>
      </AppBar>

      <TabPanel value={tabValue} index={TabId.Details}>
        <DriverDetails driverId={driverId} mode={mode} />
      </TabPanel>
      {mode === Mode.Update && (
        <>
          <TabPanel value={tabValue} index={TabId.Vehicles}>
            <Vehicles driverId={driverId} />
          </TabPanel>
          <TabPanel value={tabValue} index={TabId.SubDrivers}>
            <SubDrivers driverId={driverId as number} />
          </TabPanel>
          <Restricted to='ViewDriverContactList'>
            <TabPanel value={tabValue} index={TabId.Contacts}>
              <Contacts driverId={driverId!} />
            </TabPanel>
          </Restricted>
          <Restricted to='ViewDriverInvoices'>
            <TabPanel value={tabValue} index={TabId.Invoices}>
              <DriverInvoices driverId={driverId as number} />
            </TabPanel>
          </Restricted>
          <Restricted to='ViewDriverAccountsPayableRecords'>
            <TabPanel value={tabValue} index={TabId.AccountsPayable}>
              <DriverAccountsPayable driverId={driverId as number} />
            </TabPanel>
          </Restricted>
          <Restricted to='ViewUpdateBookingDriverRating'>
            <TabPanel value={tabValue} index={TabId.Ratings}>
              <BookingDriverRatingsTab />
            </TabPanel>
          </Restricted>
        </>
      )}
    </div>
  )
}

export default DriverTab
